import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  CheckCircle2, 
  Clock, 
  Loader2,
  AlertTriangle,
  Mail,
  Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function DeletionStatusPage() {
  const [searchParams] = useSearchParams();
  const confirmationCode = searchParams.get('id');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (confirmationCode) {
      fetchStatus();
    } else {
      setError('No confirmation code provided');
      setLoading(false);
    }
  }, [confirmationCode]);

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/facebook/deletion-status?id=${confirmationCode}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Deletion request not found. Please check your confirmation code.');
        } else {
          setError('Failed to fetch deletion status');
        }
        return;
      }
      
      const data = await response.json();
      setStatus(data);
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (statusValue) => {
    switch (statusValue) {
      case 'completed':
        return <CheckCircle2 className="w-12 h-12 text-green-600" />;
      case 'in_progress':
        return <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />;
      case 'pending':
      default:
        return <Clock className="w-12 h-12 text-yellow-600" />;
    }
  };

  const getStatusColor = (statusValue) => {
    switch (statusValue) {
      case 'completed':
        return 'bg-green-100 border-green-200';
      case 'in_progress':
        return 'bg-blue-100 border-blue-200';
      case 'pending':
      default:
        return 'bg-yellow-100 border-yellow-200';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="deletion-status-page">
      <Header />
      
      <div className="flex-1 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="p-8 bg-white border border-gray-200">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <Shield className="w-16 h-16 text-[#00D95F]" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Data Deletion Request Status
              </h1>
              <p className="text-gray-600">
                Track the status of your data deletion request
              </p>
            </div>

            {loading && (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-12 h-12 text-[#00D95F] animate-spin mb-4" />
                <p className="text-gray-600">Loading status...</p>
              </div>
            )}

            {error && !loading && (
              <div className="text-center py-8">
                <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
                <p className="text-gray-600 mb-6">{error}</p>
                <Link 
                  to="/contact" 
                  className="text-[#00D95F] hover:underline"
                >
                  Contact Support
                </Link>
              </div>
            )}

            {status && !loading && (
              <>
                {/* Status Card */}
                <div className={`rounded-lg border p-6 mb-6 ${getStatusColor(status.status)}`}>
                  <div className="flex items-center gap-4">
                    {getStatusIcon(status.status)}
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 capitalize">
                        {status.status.replace('_', ' ')}
                      </h2>
                      <p className="text-gray-700">{status.status_message}</p>
                    </div>
                  </div>
                </div>

                {/* Request Details */}
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Request Details</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Confirmation Code</span>
                      <code className="bg-gray-200 px-3 py-1 rounded text-sm font-mono">
                        {status.confirmation_code}
                      </code>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Requested On</span>
                      <span className="text-gray-900">{formatDate(status.requested_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Scheduled Completion</span>
                      <span className="text-gray-900">{formatDate(status.scheduled_deletion_date)}</span>
                    </div>
                    {status.completed_at && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Completed On</span>
                        <span className="text-green-600 font-medium">{formatDate(status.completed_at)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Data Retention Notice */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Data Retention Notice
                  </h4>
                  <p className="text-sm text-blue-700">
                    {status.data_retention_notice}
                  </p>
                </div>

                {/* Contact Information */}
                <div className="text-center pt-6 border-t border-gray-200">
                  <p className="text-gray-600 mb-2">Questions about your deletion request?</p>
                  <a 
                    href={`mailto:${status.contact_email}`}
                    className="inline-flex items-center gap-2 text-[#00D95F] hover:underline"
                  >
                    <Mail className="w-4 h-4" />
                    {status.contact_email}
                  </a>
                </div>
              </>
            )}
          </Card>

          {/* Additional Information */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>
              This page is provided in accordance with Meta's Platform Terms and GDPR requirements.
            </p>
            <div className="mt-2 space-x-4">
              <Link to="/privacy" className="hover:text-[#00D95F]">Privacy Policy</Link>
              <span>•</span>
              <Link to="/terms" className="hover:text-[#00D95F]">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
